import { useState, useEffect } from "react";
import api from "../api/client";
import { useToast } from "../hooks/use-toast";
import ActivityHistory from "./ActivityHistory";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Status badge component
function StatusBadge({ status }) {
  const config = {
    completed: { label: "Validé", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    in_progress: { label: "En cours", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
    pending: { label: "À faire", color: "bg-gray-100 text-gray-600 border-gray-200" },
    locked: { label: "Verrouillé", color: "bg-gray-100 text-gray-400 border-gray-200" },
    skipped: { label: "Ignoré", color: "bg-amber-100 text-amber-700 border-amber-200" },
  };
  const c = config[status] || config.pending;
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${c.color}`}>{c.label}</span>;
}

// Risk level badge
function RiskBadge({ level, score }) {
  const config = {
    low: { label: "Faible", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: "🟢" },
    medium: { label: "Modéré", color: "bg-amber-100 text-amber-700 border-amber-200", icon: "🟡" },
    high: { label: "Élevé", color: "bg-red-100 text-red-700 border-red-200", icon: "🔴" },
    success: { label: "Terminé", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: "✅" },
  };
  const c = config[level] || config.low;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${c.color}`}>
      <span>{c.icon}</span>
      <span className="font-medium">{c.label}</span>
      <span className="text-sm opacity-70">({score}%)</span>
    </div>
  );
}

// Notes section component
function StageNotes({ studentId, stageKey, isCoach }) {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadNotes();
  }, [studentId, stageKey]);

  const loadNotes = async () => {
    try {
      const res = await api.get(`/parcours/stage-notes/${studentId}/${stageKey}`);
      setNotes(res.data);
    } catch (e) {
      console.error("Error loading notes:", e);
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setLoading(true);
    try {
      await api.post("/parcours/stage-note", {
        student_id: studentId,
        stage_key: stageKey,
        content: newNote.trim()
      });
      setNewNote("");
      setShowForm(false);
      loadNotes();
      toast({ title: "Note ajoutée" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
    setLoading(false);
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-gray-500 uppercase">Notes ({notes.length})</span>
        {!showForm && (
          <button onClick={() => setShowForm(true)} className="text-xs text-[#1a2744] hover:underline flex items-center gap-1">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Ajouter
          </button>
        )}
      </div>
      
      {showForm && (
        <form onSubmit={handleAddNote} className="mb-3 p-3 bg-gray-50 rounded-xl">
          <textarea
            rows={2}
            placeholder="Écrire une note..."
            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-[#1a2744] resize-none"
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
          />
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => { setShowForm(false); setNewNote(""); }} className="btn-ghost text-xs px-3 py-1.5">Annuler</button>
            <button type="submit" disabled={!newNote.trim() || loading} className="btn-primary text-xs px-3 py-1.5">
              {loading ? "..." : "Enregistrer"}
            </button>
          </div>
        </form>
      )}
      
      {notes.length === 0 && !showForm && (
        <p className="text-xs text-gray-400 text-center py-2">Aucune note</p>
      )}
      
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {notes.map(note => (
          <div key={note.id} className="p-2 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-700">{note.content}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${note.author_role === "coach" ? "bg-blue-100 text-blue-700" : "bg-violet-100 text-violet-700"}`}>
                {note.author_name}
              </span>
              <span className="text-[10px] text-gray-400">
                {new Date(note.created_at).toLocaleDateString("fr-FR")}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sub-stage item component
function SubStageItem({ substage, onUpdate, onDelete, canValidate, canEdit = false, showParent = false, dragHandleProps = null }) {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: substage.title || "",
    description: substage.description || "",
    deadline: substage.deadline ? substage.deadline.slice(0, 10) : "",
  });
  const { toast } = useToast();

  const handleStatusChange = async () => {
    setLoading(true);
    try {
      const newStatus = substage.status === "completed" ? "pending" : "completed";
      await api.put(`/parcours/substage/${substage.id}`, { status: newStatus });
      onUpdate();
    } catch (e) {
      console.error("Error updating substage:", e);
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de mettre à jour le statut" });
    }
    setLoading(false);
  };

  const handleSaveEdit = async (e) => {
    e?.preventDefault();
    if (!editForm.title.trim()) return;
    setLoading(true);
    try {
      const payload = {
        title: editForm.title.trim(),
        description: editForm.description.trim() || null,
        deadline: editForm.deadline || null,
      };
      await api.put(`/parcours/substage/${substage.id}`, payload);
      setIsEditing(false);
      onUpdate();
      toast({ title: "Sous-étape modifiée" });
    } catch (e) {
      console.error("Error saving substage:", e);
      toast({ variant: "destructive", title: "Erreur", description: "Impossible d'enregistrer les modifications" });
    }
    setLoading(false);
  };

  const handleCancelEdit = () => {
    setEditForm({
      title: substage.title || "",
      description: substage.description || "",
      deadline: substage.deadline ? substage.deadline.slice(0, 10) : "",
    });
    setIsEditing(false);
  };

  const isOverdue = substage.deadline && substage.status !== "completed" && new Date(substage.deadline) < new Date();

  // ===== Edit mode =====
  if (isEditing && canEdit) {
    return (
      <form
        onSubmit={handleSaveEdit}
        className="p-3 rounded-xl border bg-white space-y-2"
        style={{ borderColor: "var(--gold-soft)" }}
        data-testid={`substage-edit-form-${substage.id}`}
      >
        <input
          type="text"
          value={editForm.title}
          onChange={e => setEditForm({ ...editForm, title: e.target.value })}
          placeholder="Titre de la sous-étape"
          className="input-dark w-full text-sm"
          autoFocus
          required
          data-testid={`substage-edit-title-${substage.id}`}
        />
        <textarea
          value={editForm.description}
          onChange={e => setEditForm({ ...editForm, description: e.target.value })}
          placeholder="Description (optionnel)"
          className="input-dark w-full text-sm resize-y"
          rows={2}
          data-testid={`substage-edit-description-${substage.id}`}
        />
        <input
          type="date"
          value={editForm.deadline}
          onChange={e => setEditForm({ ...editForm, deadline: e.target.value })}
          className="input-dark w-full text-sm"
          data-testid={`substage-edit-deadline-${substage.id}`}
        />
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={handleCancelEdit}
            className="btn-ghost text-xs"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn-primary text-xs"
            disabled={loading || !editForm.title.trim()}
            data-testid={`substage-save-${substage.id}`}
          >
            {loading ? "Sauvegarde…" : "Enregistrer"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
      substage.status === "completed" ? "bg-emerald-50/50 border-emerald-200" :
      isOverdue ? "bg-red-50/50 border-red-200" :
      "bg-white border-gray-200 hover:border-gray-300"
    }`}>
      {dragHandleProps && (
        <button
          type="button"
          {...dragHandleProps}
          className="p-1 text-gray-300 hover:text-gold-dark cursor-grab active:cursor-grabbing flex-shrink-0"
          title="Glisser pour réordonner"
          data-testid={`substage-drag-handle-${substage.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M10 6a2 2 0 11-4 0 2 2 0 014 0zM10 12a2 2 0 11-4 0 2 2 0 014 0zM10 18a2 2 0 11-4 0 2 2 0 014 0zM18 6a2 2 0 11-4 0 2 2 0 014 0zM18 12a2 2 0 11-4 0 2 2 0 014 0zM18 18a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </button>
      )}
      <button
        onClick={handleStatusChange}
        disabled={loading}
        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
          substage.status === "completed" 
            ? "bg-emerald-500 border-emerald-500 text-white" 
            : "border-gray-300 hover:border-emerald-500"
        }`}
      >
        {substage.status === "completed" && (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
      
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${substage.status === "completed" ? "text-gray-500 line-through" : "text-gray-900"}`}>
          {substage.title}
        </p>
        {showParent && substage.parent_stage_title && (
          <p className="text-xs text-[#1a2744] mt-0.5">→ {substage.parent_stage_title}</p>
        )}
        {substage.description && (
          <p className="text-xs text-gray-500 mt-0.5">{substage.description}</p>
        )}
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {substage.deadline && (
            <span className={`text-xs ${isOverdue ? "text-red-600 font-medium" : "text-gray-400"}`}>
              📅 {new Date(substage.deadline).toLocaleDateString("fr-FR")}
              {isOverdue && " (en retard)"}
            </span>
          )}
          {substage.validated_at && (
            <span className="text-xs text-emerald-600">
              ✓ {new Date(substage.validated_at).toLocaleDateString("fr-FR")}
            </span>
          )}
        </div>
      </div>

      {canEdit && (
        <button
          onClick={() => setIsEditing(true)}
          className="p-1.5 text-gray-400 hover:text-gold-dark hover:bg-gold-bg rounded-lg transition-colors flex-shrink-0"
          title="Modifier"
          data-testid={`substage-edit-${substage.id}`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}

      {canValidate && onDelete && (
        <button
          onClick={() => onDelete(substage.id)}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
          title="Supprimer"
          data-testid={`substage-delete-${substage.id}`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
}

// Sortable wrapper for sub-stage (drag & drop) — only enables DnD when canDrag=true
function SortableSubStage({ id, canDrag, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !canDrag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`substage-sortable-${id}`}>
      {typeof children === "function"
        ? children({
            dragHandleProps: canDrag ? { ...attributes, ...listeners } : null,
          })
        : children}
    </div>
  );
}

// Main stage card component
function MainStageCard({ stage, studentId, isCoach, onUpdate, onAddSubStage, onValidateStage, onSkipStage, onUnvalidateStage, onDeleteStage, onRenameStage }) {
  const [expanded, setExpanded] = useState(stage.status === "in_progress");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSubStage, setNewSubStage] = useState({ title: "", description: "", deadline: "" });
  const [loading, setLoading] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(stage.title || "");
  const [renameSaving, setRenameSaving] = useState(false);

  // dnd-kit sensors (must be at top level of component)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAddSubStage = async (e) => {
    e.preventDefault();
    if (!newSubStage.title.trim()) return;
    setLoading(true);
    try {
      await onAddSubStage(stage.key, newSubStage);
      setNewSubStage({ title: "", description: "", deadline: "" });
      setShowAddForm(false);
    } catch (err) {
      console.error("Error adding substage:", err);
    }
    setLoading(false);
  };

  const handleRenameSave = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const trimmed = (titleDraft || "").trim();
    if (!trimmed) return;
    setRenameSaving(true);
    try {
      await onRenameStage?.(stage, trimmed);
      setEditingTitle(false);
    } finally {
      setRenameSaving(false);
    }
  };

  const handleRenameReset = async (e) => {
    e?.stopPropagation();
    setRenameSaving(true);
    try {
      await onRenameStage?.(stage, null); // null => reset
      setEditingTitle(false);
      setTitleDraft(stage.title_default || stage.title);
    } finally {
      setRenameSaving(false);
    }
  };

  const handleReorder = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = (stage.sub_stages || []).map(s => s.id);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const newOrder = arrayMove(ids, oldIdx, newIdx);
    try {
      await api.put("/parcours/substages/reorder", {
        student_id: studentId,
        main_stage_key: stage.key,
        ordered_ids: newOrder,
      });
      onUpdate();
    } catch (err) {
      console.error("Reorder failed:", err);
      onUpdate();
    }
  };

  const statusIcon = {
    completed: <svg className="w-6 h-6 text-emerald-500" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>,
    in_progress: <div className="w-6 h-6 rounded-full border-4 border-cyan-500 border-t-transparent animate-spin" />,
    pending: <div className="w-6 h-6 rounded-full border-2 border-gray-300" />,
    locked: <svg className="w-6 h-6 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
    skipped: <svg className="w-6 h-6 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>,
  };

  const isCustom = stage.key?.startsWith("custom_");

  return (
    <div className={`glass-card rounded-2xl overflow-hidden transition-all ${stage.status === "locked" ? "opacity-60" : ""}`}>
      {/* Header */}
      <div 
        className={`p-5 cursor-pointer ${
          stage.status === "completed" ? "bg-emerald-50/50" :
          stage.status === "in_progress" ? "bg-cyan-50/50" :
          stage.status === "skipped" ? "bg-amber-50/50" : ""
        }`}
        onClick={() => stage.status !== "locked" && setExpanded(!expanded)}
        data-testid={`stage-header-${stage.key}`}
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-0.5">
            {statusIcon[stage.status]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-medium text-gray-400">Étape {stage.order}</span>
              {isCustom && <span className="text-xs px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded">Personnalisée</span>}
              {stage.optional && <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">Optionnelle</span>}
              <StatusBadge status={stage.status} />
            </div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              {editingTitle && isCoach && !isCustom ? (
                <form
                  onSubmit={handleRenameSave}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-2 flex-1"
                  data-testid={`stage-rename-form-${stage.key}`}
                >
                  <input
                    type="text"
                    value={titleDraft}
                    onChange={e => setTitleDraft(e.target.value)}
                    autoFocus
                    className="input-dark text-base font-bold flex-1 min-w-0"
                    placeholder={stage.title_default || "Titre de l'étape"}
                    data-testid={`stage-rename-input-${stage.key}`}
                  />
                  <button
                    type="submit"
                    disabled={renameSaving || !titleDraft.trim()}
                    className="btn-primary text-xs px-3 py-1.5"
                    data-testid={`stage-rename-save-${stage.key}`}
                  >
                    {renameSaving ? "…" : "OK"}
                  </button>
                  {stage.is_renamed && (
                    <button
                      type="button"
                      onClick={handleRenameReset}
                      className="btn-ghost text-xs px-2 py-1.5"
                      title="Réinitialiser au titre par défaut"
                      data-testid={`stage-rename-reset-${stage.key}`}
                    >
                      ↺
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditingTitle(false); setTitleDraft(stage.title); }}
                    className="btn-ghost text-xs px-2 py-1.5"
                  >
                    ✕
                  </button>
                </form>
              ) : (
                <>
                  <span data-testid={`stage-title-${stage.key}`}>{stage.title}</span>
                  {stage.is_renamed && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                      style={{ color: "var(--gold-dark)", background: "var(--paper-2)", letterSpacing: "0.08em" }}
                      title={`Titre par défaut : ${stage.title_default}`}
                    >
                      renommée
                    </span>
                  )}
                  {isCoach && !isCustom && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setTitleDraft(stage.title); setEditingTitle(true); }}
                      className="p-1 text-gray-400 hover:text-gold-dark rounded transition-colors"
                      title="Renommer l'étape pour cet élève"
                      data-testid={`stage-rename-btn-${stage.key}`}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                </>
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-1">{stage.description}</p>
            
            {/* Sub-stages progress */}
            {stage.sub_stages_total > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${(stage.sub_stages_completed / stage.sub_stages_total) * 100}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">{stage.sub_stages_completed}/{stage.sub_stages_total}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isCustom && isCoach && (
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteStage?.(stage.key.replace("custom_", "")); }}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
            {stage.status !== "locked" && (
              <svg className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && stage.status !== "locked" && (
        <div className="px-5 pb-5 border-t border-gray-100">
          {/* Sub-stages list */}
          <div className="mt-4 space-y-2">
            {(!stage.sub_stages || stage.sub_stages.length === 0) ? (
              <p className="text-sm text-gray-400 text-center py-4">Aucune sous-étape pour le moment</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleReorder}>
                <SortableContext items={stage.sub_stages.map(s => s.id)} strategy={verticalListSortingStrategy}>
                  {stage.sub_stages.map(ss => (
                    <SortableSubStage key={ss.id} id={ss.id} canDrag={isCoach}>
                      {({ dragHandleProps }) => (
                        <SubStageItem
                          substage={ss}
                          onUpdate={onUpdate}
                          onDelete={isCoach ? async (id) => {
                            await api.delete(`/parcours/substage/${id}`);
                            onUpdate();
                          } : null}
                          canValidate={true}
                          canEdit={isCoach}
                          dragHandleProps={dragHandleProps}
                        />
                      )}
                    </SortableSubStage>
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>

          {/* Add sub-stage form (coach only) */}
          {isCoach && (
            <div className="mt-4">
              {showAddForm ? (
                <form onSubmit={handleAddSubStage} className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <input
                    type="text"
                    placeholder="Titre de la sous-étape *"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-[#1a2744]"
                    value={newSubStage.title}
                    onChange={e => setNewSubStage({ ...newSubStage, title: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Description (optionnelle)"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-[#1a2744]"
                    value={newSubStage.description}
                    onChange={e => setNewSubStage({ ...newSubStage, description: e.target.value })}
                  />
                  <input
                    type="date"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-[#1a2744]"
                    value={newSubStage.deadline}
                    onChange={e => setNewSubStage({ ...newSubStage, deadline: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowAddForm(false)} className="btn-ghost flex-1">Annuler</button>
                    <button type="submit" disabled={!newSubStage.title.trim() || loading} className="btn-primary flex-1">
                      {loading ? "..." : "Ajouter"}
                    </button>
                  </div>
                </form>
              ) : (
                <button
                  onClick={() => setShowAddForm(true)}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-[#1a2744] hover:text-[#1a2744] transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Ajouter une sous-étape
                </button>
              )}
            </div>
          )}

          {/* Validate stage button (coach only) */}
          {isCoach && stage.status !== "completed" && stage.status !== "skipped" && (
            <div className="mt-4 flex gap-2">
              {stage.optional && (
                <button
                  onClick={() => onSkipStage(stage.key)}
                  className="flex-1 btn-ghost text-amber-600 hover:bg-amber-50"
                  data-testid={`skip-stage-btn-${stage.key}`}
                >
                  Ignorer cette étape
                </button>
              )}
              <button
                onClick={() => onValidateStage(stage.key)}
                className="flex-1 btn-secondary"
                data-testid={`validate-stage-btn-${stage.key}`}
              >
                Valider l'étape
              </button>
            </div>
          )}

          {/* Un-validate / reactivate stage (coach only) */}
          {isCoach && (stage.status === "completed" || stage.status === "skipped") && (
            <div className="mt-4">
              <button
                onClick={() => onUnvalidateStage(stage.key, stage.status)}
                className="w-full btn-ghost text-red-600 hover:bg-red-50"
                data-testid={`unvalidate-stage-btn-${stage.key}`}
              >
                {stage.status === "skipped" ? "Réactiver cette étape" : "Dévalider l'étape"}
              </button>
            </div>
          )}

          {/* Notes section */}
          <StageNotes studentId={studentId} stageKey={stage.key} isCoach={isCoach} />
        </div>
      )}
    </div>
  );
}

// Create Custom Stage Modal
function CreateCustomStageModal({ studentId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try {
      await api.post("/parcours/custom-stage", {
        student_id: studentId,
        title: title.trim(),
        description: description.trim() || null
      });
      toast({ title: "Étape créée" });
      onCreated();
      onClose();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur lors de la création" });
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
        <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
          Nouvelle étape personnalisée
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Cette étape sera insérée après la dernière étape validée.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            data-testid="custom-stage-title-input"
            placeholder="Titre de l'étape *"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#1a2744]"
            value={title}
            onChange={e => setTitle(e.target.value)}
          />
          <textarea
            rows={3}
            placeholder="Description (optionnelle)"
            className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#1a2744] resize-none"
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">Annuler</button>
            <button type="submit" data-testid="custom-stage-submit-btn" disabled={!title.trim() || loading} className="btn-primary flex-1">
              {loading ? "Création..." : "Créer l'étape"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Sortable wrapper for main stage cards (coach drag & drop)
function SortableStageCard({ stageKey, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stageKey });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`relative pl-7 ${isDragging ? "opacity-75 z-20" : ""}`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute left-0 top-7 p-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none"
        title="Déplacer l'étape"
        data-testid={`stage-drag-handle-${stageKey}`}
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/>
          <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
          <circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/>
        </svg>
      </button>
      {children}
    </div>
  );
}

// Main Parcours View component with 3 tabs
export default function ParcoursView({ studentId, isCoach = false }) {
  const [activeTab, setActiveTab] = useState("parcours");
  const [parcours, setParcours] = useState(null);
  const [allSubstages, setAllSubstages] = useState([]);
  const [validatedItems, setValidatedItems] = useState({ stages: [], substages: [] });
  const [riskScore, setRiskScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateStage, setShowCreateStage] = useState(false);
  const { toast } = useToast();

  const stageSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleStageDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const keys = parcours.stages.map(s => s.key);
    const oldIdx = keys.indexOf(active.id);
    const newIdx = keys.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const newOrder = arrayMove(keys, oldIdx, newIdx);
    setParcours(p => ({ ...p, stages: newOrder.map(k => p.stages.find(s => s.key === k)) }));
    try {
      await api.put("/parcours/stages/reorder", { student_id: studentId, ordered_keys: newOrder });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de réordonner les étapes" });
    }
    loadData();
  };

  const loadData = async () => {
    try {
      const requests = [
        api.get(`/parcours/student/${studentId}`),
        api.get(`/parcours/substages/${studentId}`),
        api.get(`/parcours/validated/${studentId}`),
      ];
      if (isCoach) {
        requests.push(api.get(`/parcours/risk-score/${studentId}`));
      }
      
      const results = await Promise.all(requests);
      setParcours(results[0].data);
      setAllSubstages(results[1].data);
      setValidatedItems(results[2].data);
      if (isCoach && results[3]) {
        setRiskScore(results[3].data);
      }
    } catch (e) {
      console.error("Error loading parcours:", e);
      setError("Erreur lors du chargement du parcours");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (studentId) loadData();
  }, [studentId]);

  const handleAddSubStage = async (stageKey, data) => {
    await api.post("/parcours/substage", {
      student_id: studentId,
      main_stage_key: stageKey,
      ...data
    });
    loadData();
  };

  const handleValidateStage = async (stageKey) => {
    try {
      await api.post("/parcours/validate", {
        student_id: studentId,
        stage_key: stageKey,
        validated: true,
        skip_optional: false
      });
      loadData();
      toast({ title: "Étape validée" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: e?.response?.data?.detail || "Impossible de valider l'étape"
      });
    }
  };

  const handleSkipStage = async (stageKey) => {
    try {
      await api.post("/parcours/validate", {
        student_id: studentId,
        stage_key: stageKey,
        validated: false,
        skip_optional: true
      });
      loadData();
      toast({ title: "Étape ignorée" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: e?.response?.data?.detail || "Impossible d'ignorer l'étape"
      });
    }
  };

  const handleUnvalidateStage = async (stageKey, currentStatus) => {
    const label = currentStatus === "skipped" ? "Réactiver cette étape ?" : "Dévalider cette étape ? Elle repassera en cours.";
    if (!window.confirm(label)) return;
    try {
      await api.post("/parcours/validate", {
        student_id: studentId,
        stage_key: stageKey,
        validated: false,
        skip_optional: false
      });
      loadData();
      toast({ title: currentStatus === "skipped" ? "Étape réactivée" : "Étape dévalidée" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: e?.response?.data?.detail || "Impossible de dévalider l'étape"
      });
    }
  };

  const handleDeleteCustomStage = async (stageId) => {
    if (!window.confirm("Supprimer cette étape personnalisée et toutes ses sous-étapes ?")) return;
    try {
      await api.delete(`/parcours/custom-stage/${stageId}`);
      loadData();
      toast({ title: "Étape supprimée" });
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  const handleDeleteSubstage = async (id) => {
    try {
      await api.delete(`/parcours/substage/${id}`);
      loadData();
    } catch (e) {
      console.error("Error deleting substage:", e);
    }
  };

  const handleRenameStage = async (stage, newTitle) => {
    if (stage.key?.startsWith("custom_")) {
      const customId = stage.key.replace("custom_", "");
      try {
        await api.put(`/parcours/custom-stage/${customId}`, { title: newTitle });
        loadData();
        toast({ title: "Étape renommée" });
      } catch (e) {
        toast({ variant: "destructive", title: "Erreur" });
      }
      return;
    }
    try {
      if (newTitle === null) {
        await api.delete(`/parcours/stage-override/${studentId}/${stage.key}`);
        toast({ title: "Titre par défaut restauré" });
      } else {
        await api.put(`/parcours/stage-override/${studentId}/${stage.key}`, { title: newTitle });
        toast({ title: "Étape renommée" });
      }
      loadData();
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: e?.response?.data?.detail || "Impossible de renommer" });
    }
  };

  // Filter substages that are not completed (for "Sous-étapes" tab)
  const pendingSubstages = allSubstages.filter(s => s.status !== "completed");

  const tabs = [
    { key: "parcours", label: "Mon Parcours", icon: "M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" },
    { key: "substages", label: "Sous-étapes", icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" },
    { key: "validated", label: "Validées", icon: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" },
    { key: "history", label: "Historique", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-[#1a2744] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card rounded-2xl p-6 text-center">
        <p className="text-red-500">{error}</p>
        <button onClick={loadData} className="btn-ghost mt-4">Réessayer</button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with progress */}
      <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Parcours Investisseur
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {parcours?.completed_stages || 0} sur {parcours?.total_stages || 0} étapes validées
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isCoach && riskScore && <RiskBadge level={riskScore.level} score={riskScore.score} />}
            {isCoach && (
              <button
                onClick={() => setShowCreateStage(true)}
                className="btn-primary text-sm px-4 py-2 flex items-center gap-2"
                data-testid="create-stage-btn"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nouvelle étape
              </button>
            )}
          </div>
        </div>
        
        {/* Overall progress bar */}
        <div className="relative">
          <div className="h-2 sm:h-3 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#1a2744] to-emerald-500 transition-all duration-700"
              style={{ width: `${parcours?.overall_progress || 0}%` }}
            />
          </div>
          <span className="absolute right-0 -top-5 sm:-top-6 text-xs sm:text-sm font-bold text-[#1a2744]" data-testid="parcours-overall-progress">
            {parcours?.overall_progress || 0}%
          </span>
        </div>

        {/* Risk factors (coach only) */}
        {isCoach && riskScore?.factors?.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Facteurs de risque</p>
            <div className="flex flex-wrap gap-2">
              {riskScore.factors.map((f, i) => (
                <span key={i} className={`text-xs px-2 py-1 rounded-lg ${
                  f.impact === "high" ? "bg-red-100 text-red-700" :
                  f.impact === "medium" ? "bg-amber-100 text-amber-700" :
                  "bg-gray-100 text-gray-600"
                }`}>
                  {f.name}: {f.value}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-white rounded-xl border border-gray-200 shadow-sm">
        {tabs.map(t => (
          <button
            key={t.key}
            data-testid={`parcours-tab-${t.key}`}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
              activeTab === t.key 
                ? "bg-[#1a2744] text-white shadow-sm" 
                : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"
            }`}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={t.icon} />
            </svg>
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "parcours" && (
        <div className="space-y-3 sm:space-y-4">
          {isCoach ? (
            <DndContext sensors={stageSensors} collisionDetection={closestCenter} onDragEnd={handleStageDragEnd}>
              <SortableContext items={parcours?.stages?.map(s => s.key) || []} strategy={verticalListSortingStrategy}>
                {parcours?.stages?.map(stage => (
                  <SortableStageCard key={stage.key} stageKey={stage.key}>
                    <MainStageCard
                      stage={stage}
                      studentId={studentId}
                      isCoach={isCoach}
                      onUpdate={loadData}
                      onAddSubStage={handleAddSubStage}
                      onValidateStage={handleValidateStage}
                      onSkipStage={handleSkipStage}
                      onUnvalidateStage={handleUnvalidateStage}
                      onDeleteStage={handleDeleteCustomStage}
                      onRenameStage={handleRenameStage}
                    />
                  </SortableStageCard>
                ))}
              </SortableContext>
            </DndContext>
          ) : (
            parcours?.stages?.map(stage => (
              <MainStageCard
                key={stage.key}
                stage={stage}
                studentId={studentId}
                isCoach={isCoach}
                onUpdate={loadData}
                onAddSubStage={handleAddSubStage}
                onValidateStage={handleValidateStage}
                onSkipStage={handleSkipStage}
                onUnvalidateStage={handleUnvalidateStage}
                onDeleteStage={handleDeleteCustomStage}
                onRenameStage={handleRenameStage}
              />
            ))
          )}
        </div>
      )}

      {activeTab === "substages" && (
        <div className="space-y-3">
          <div className="glass-card rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Sous-étapes en cours</h3>
            <p className="text-xs text-gray-500">{pendingSubstages.length} sous-étape{pendingSubstages.length !== 1 ? "s" : ""} à compléter</p>
          </div>
          
          {pendingSubstages.length === 0 ? (
            <div className="glass-card rounded-xl p-8 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">Toutes les sous-étapes sont complétées !</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pendingSubstages.map(ss => (
                <SubStageItem
                  key={ss.id}
                  substage={ss}
                  onUpdate={loadData}
                  onDelete={isCoach ? handleDeleteSubstage : null}
                  canValidate={true}
                  canEdit={isCoach}
                  showParent={true}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "validated" && (
        <div className="space-y-4">
          {/* Validated main stages */}
          {validatedItems.stages?.length > 0 && (
            <div className="glass-card rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Étapes principales validées</h3>
              <div className="space-y-2">
                {validatedItems.stages.map(item => (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-xl border border-emerald-200">
                    <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-500">
                        {item.skipped ? "Ignorée" : "Validée"} le {new Date(item.validated_at || item.skipped_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                    {item.skipped && <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">Ignorée</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Validated sub-stages */}
          {validatedItems.substages?.length > 0 && (
            <div className="glass-card rounded-xl p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Sous-étapes complétées</h3>
              <div className="space-y-2">
                {validatedItems.substages.map(item => (
                  <div key={item.id} className="flex items-center gap-3 p-3 bg-emerald-50/50 rounded-xl border border-emerald-200">
                    <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{item.title}</p>
                      <p className="text-xs text-[#1a2744]">→ {item.parent_stage_title}</p>
                      {item.validated_at && (
                        <p className="text-xs text-gray-500">Complétée le {new Date(item.validated_at).toLocaleDateString("fr-FR")}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!validatedItems.stages?.length && !validatedItems.substages?.length) && (
            <div className="glass-card rounded-xl p-8 text-center">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">Aucune étape validée pour le moment</p>
            </div>
          )}
        </div>
      )}

      {activeTab === "history" && (
        <div className="glass-card rounded-xl sm:rounded-2xl p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                Historique d'activité
              </h3>
              <p className="text-xs text-gray-500">Toutes les actions sur le parcours</p>
            </div>
          </div>
          <ActivityHistory studentId={studentId} />
        </div>
      )}

      {/* Create Custom Stage Modal */}
      {showCreateStage && (
        <CreateCustomStageModal
          studentId={studentId}
          onClose={() => setShowCreateStage(false)}
          onCreated={loadData}
        />
      )}
    </div>
  );
}
